// src/services/api.ts
import axios, { AxiosHeaders, AxiosError } from 'axios';
import { API_BASE } from '../config/environment';

export { API_BASE };
export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

let refreshPromise: Promise<any> | null = null;

api.interceptors.request.use((config) => {
    // Markiere Requests
    // @ts-expect-error debug flag
    config.__fromApiInstance = true;

    const headers = (config.headers ??= new AxiosHeaders(config.headers));
    const m = document.cookie.match(/(?:^|;\s*)device-t=([^;]*)/);
    const fp = m ? decodeURIComponent(m[1]) : localStorage.getItem('device-t') || '';

    // Only add device fingerprint for authenticated endpoints
    // Skip for public endpoints and auth status checks
    const url = config.url || '';
    const isPublicEndpoint = url.includes('/public') || url.includes('/auth/status') || url.includes('/auth/refresh');

    if (fp && !isPublicEndpoint) {
        headers.set('x-device-fp', fp);
    }
    headers.set('Accept', 'application/json');

    return config;
});

api.interceptors.response.use(
    (res) => res,
    async (err: AxiosError) => {
        const cfg: any = err.config || {};
        const status = err.response?.status;
        const url = cfg?.url as string | undefined;

        // Wenn der Request NICHT von unserer Instanz kam, hier raus (zeigt Import-/Pfadproblem)
        if (cfg?.__fromApiInstance !== true) {
            return Promise.reject(err);
        }

        const isRefresh = Boolean(url && url.endsWith('/auth/refresh'));
        const isAuthStatus = Boolean(url && url.includes('/auth/status'));

        // Skip refresh for auth/status and auth/refresh endpoints
        if (status !== 401 || isRefresh || isAuthStatus) return Promise.reject(err);

        if (cfg._retry) {
            return Promise.reject(err);
        }
        cfg._retry = true;

        if (!refreshPromise) {
            refreshPromise = api.post('/auth/refresh', {}, {
                validateStatus: (s) => s === 200 || s === 401,
            }).finally(() => {
                refreshPromise = null;
            });
        }

        try {
            const refreshResponse = await refreshPromise;
            // Only retry if refresh was successful (200)
            if (refreshResponse.status === 200) {
                return api.request(cfg);
            }
            return Promise.reject(err);
        } catch (e) {
            return Promise.reject(e);
        }
    }
);


let ensureSessionPromise: Promise<void> | null = null;

export const ensureFreshSession = async (): Promise<void> => {
  if (ensureSessionPromise) return ensureSessionPromise;

  ensureSessionPromise = (async () => {
    try {
      const status = await api.get('/auth/status', {
        validateStatus: (s) => s === 200 || s === 401,
      });
      if (status.data?.authenticated === true) return;
      if (status.status === 401) return;

      const refresh = await api.post('/auth/refresh', {}, {
        validateStatus: (s) => s === 200 || s === 401,
      });
      if (refresh.status !== 200) return;

      await api.get('/auth/status', {
        validateStatus: (s) => s === 200 || s === 401,
      });
    } catch {
      // Silently fail — the actual REST call will handle 401 via interceptor
    }
  })().finally(() => {
    ensureSessionPromise = null;
  });

  return ensureSessionPromise;
};

// Public metrics API call (no auth required)
export interface PropMetricsData {
  provider_id: string;
  funded_capital: {
    sum: number;
    label: string;
  };
  funded_accounts: {
    count: number;
    label: string;
  };
  trades_executed: {
    count: number;
    label: string;
  };
}

export const fetchPropMetrics = async (): Promise<PropMetricsData> => {
  const response = await api.get<PropMetricsData>('/providers/prop-metrics/public');
  return response.data;
};

export default api;